import { cn } from "@local/utils/src/cn"
import { getCdnUrl } from "@local/utils/src/getCdnUrl"
import type { CSSProperties, ReactNode } from "react"

interface Props {
  children: ReactNode
  className?: string
  touchEdges?: boolean
  fullWidthBgColor?: string
  fullWidthBgImageUrl?: string
  id?: string
  style?: CSSProperties
  role?: string
}

export const ContentBlock = ({
  id,
  children,
  className,
  touchEdges,
  fullWidthBgColor,
  style,
  fullWidthBgImageUrl,
  role,
}: Props) => {
  return (
    <div
      className={cn(
        "content-block w-full",
        fullWidthBgColor ? fullWidthBgColor : ""
      )}
      id={id ?? ""}
      style={
        fullWidthBgImageUrl
          ? {
              ...style,
              backgroundImage: `url(${getCdnUrl({ src: fullWidthBgImageUrl })})`,
            }
          : style
      }
      role={role}
      // Only when this renders a `style` attribute at all — from the prop, from
      // the background image, or from both. When this block sits under
      // a dehydrated Suspense boundary, React hides it by writing
      // `style.display = "none"` and later clears it again
      // (`hideOrUnhideDehydratedBoundary`). Touching `.style` makes the browser
      // reserialize the whole attribute — `a:b;c:d` becomes `a: b; c: d;` — so by
      // hydration the DOM string no longer matches React's own compact
      // serialization and it reports an attribute mismatch. The declarations and
      // their values are identical; only the CSS text formatting differs, so
      // there is nothing to patch up.
      suppressHydrationWarning={Boolean(style || fullWidthBgImageUrl)}
    >
      <div
        className={cn(touchEdges ? "w-full" : "mx-auto max-w-7xl", className)}
      >
        {children}
      </div>
    </div>
  )
}
